import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  CheckCircle2,
  ArrowRight,
  Home,
  Building2,
  BedDouble,
  HardHat,
  Phone,
  Info
} from "lucide-react";

const residentialPricing = [
  {
    name: "Standard Clean",
    description: "Regular maintenance cleaning",
    price: "From $120",
    frequency: "per visit",
    popular: false,
    features: [
      "Dusting all accessible surfaces",
      "Vacuuming and mopping floors",
      "Kitchen counter cleaning",
      "Bathroom cleaning",
      "Trash removal",
      "Bed making"
    ]
  },
  {
    name: "Deep Clean",
    description: "Thorough top-to-bottom cleaning",
    price: "From $200",
    frequency: "per visit",
    popular: true,
    features: [
      "Everything in Standard Clean",
      "Inside cabinet cleaning",
      "Appliance exterior cleaning",
      "Baseboard cleaning",
      "Light fixture dusting",
      "Window sill cleaning",
      "Door frame cleaning"
    ]
  },
  {
    name: "Move In/Out",
    description: "Complete property cleaning",
    price: "From $300",
    frequency: "per visit",
    popular: false,
    features: [
      "Everything in Deep Clean",
      "Inside oven cleaning",
      "Inside refrigerator cleaning",
      "Interior window cleaning",
      "Closet cleaning",
      "Wall spot cleaning",
      "Switch plate cleaning"
    ]
  }
];

const commercialPricing = [
  {
    name: "Basic Office",
    description: "Essential office cleaning",
    price: "From $150",
    frequency: "per visit",
    popular: false,
    features: [
      "Desk and workstation cleaning",
      "Floor vacuuming/mopping",
      "Trash and recycling removal",
      "Kitchen/break room cleaning",
      "Restroom sanitization",
      "Common area cleaning"
    ]
  },
  {
    name: "Professional",
    description: "Comprehensive office care",
    price: "From $250",
    frequency: "per visit",
    popular: true,
    features: [
      "Everything in Basic Office",
      "Conference room detailing",
      "Reception area polishing",
      "High-touch surface disinfection",
      "Interior glass cleaning",
      "Kitchen appliance cleaning"
    ]
  },
  {
    name: "Enterprise",
    description: "Full-service commercial cleaning",
    price: "Custom",
    frequency: "quote",
    popular: false,
    features: [
      "Everything in Professional",
      "Custom cleaning schedule",
      "Dedicated team assignment",
      "Monthly deep cleaning",
      "Carpet care included",
      "24/7 availability"
    ]
  }
];

const airbnbPricing = [
  {
    name: "Quick Turnaround",
    description: "Between guest cleaning",
    price: "From $100",
    frequency: "per turnover",
    popular: false,
    features: [
      "Complete surface cleaning",
      "Linen change and bed making",
      "Bathroom reset",
      "Kitchen reset",
      "Trash removal",
      "Amenity restocking check"
    ]
  },
  {
    name: "Full Service",
    description: "Comprehensive turnover",
    price: "From $150",
    frequency: "per turnover",
    popular: true,
    features: [
      "Everything in Quick Turnaround",
      "Deep bathroom cleaning",
      "Appliance cleaning",
      "Floor mopping",
      "Linen laundering included",
      "Quality photo check"
    ]
  },
  {
    name: "Premium Host",
    description: "White-glove service",
    price: "From $200",
    frequency: "per turnover",
    popular: false,
    features: [
      "Everything in Full Service",
      "Welcome basket setup",
      "Guest guide placement",
      "Damage inspection report",
      "Inventory management",
      "Priority scheduling"
    ]
  }
];

const renovationPricing = [
  {
    name: "Light Renovation",
    description: "Minor updates and painting",
    price: "From $250",
    frequency: "per project",
    popular: false,
    features: [
      "Dust removal from surfaces",
      "Floor cleaning",
      "Paint splatter removal",
      "Fixture cleaning",
      "Window cleaning",
      "Final walkthrough"
    ]
  },
  {
    name: "Standard Renovation",
    description: "Kitchen/bath remodels",
    price: "From $400",
    frequency: "per project",
    popular: true,
    features: [
      "Everything in Light Renovation",
      "Construction debris removal",
      "HVAC vent cleaning",
      "Cabinet interior cleaning",
      "Grout and tile cleaning",
      "Detailed inspection"
    ]
  },
  {
    name: "Major Construction",
    description: "Full home renovations",
    price: "Custom",
    frequency: "quote",
    popular: false,
    features: [
      "Everything in Standard",
      "Multi-phase cleaning",
      "Heavy debris removal",
      "Industrial equipment",
      "Exterior pressure washing",
      "Project management"
    ]
  }
];

const pricingCategories = [
  { value: "residential", label: "Residential", icon: Home, data: residentialPricing },
  { value: "commercial", label: "Commercial", icon: Building2, data: commercialPricing },
  { value: "airbnb", label: "Airbnb", icon: BedDouble, data: airbnbPricing },
  { value: "renovation", label: "Post-Renovation", icon: HardHat, data: renovationPricing }
];

export default function PricingPage() {
  const [activeCategory, setActiveCategory] = useState("residential");

  const currentPricing = pricingCategories.find(c => c.value === activeCategory)?.data || residentialPricing;

  return (
    <div data-testid="pricing-page" className="bg-[#FAFAF9]">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-[#064E3B]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-widest font-semibold text-[#C0A062] mb-4">
              Pricing
            </p>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-[1.1] text-white mb-6">
              Transparent, Competitive Pricing
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-white/80">
              Quality cleaning services at fair prices. Choose the package that fits your needs, or contact us for a custom quote.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          {/* Category Tabs */}
          <Tabs defaultValue="residential" className="mb-12" onValueChange={setActiveCategory}>
            <TabsList className="flex flex-wrap justify-center gap-2 bg-transparent h-auto p-0 mb-12">
              {pricingCategories.map((category) => (
                <TabsTrigger
                  key={category.value}
                  value={category.value}
                  data-testid={`pricing-tab-${category.value}`}
                  className="flex items-center gap-2 px-6 py-3 rounded-full border border-[#E7E5E4] data-[state=active]:bg-[#064E3B] data-[state=active]:text-white data-[state=active]:border-[#064E3B] text-[#78716C] hover:border-[#064E3B] transition-colors"
                >
                  <category.icon className="w-4 h-4" />
                  {category.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {pricingCategories.map((category) => (
              <TabsContent key={category.value} value={category.value}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {category.data.map((plan, index) => (
                    <Card
                      key={plan.name}
                      className={`relative bg-white border-2 ${
                        plan.popular
                          ? "border-[#C0A062] shadow-xl pricing-popular"
                          : "border-[#E7E5E4] hover:border-[#064E3B]/50"
                      } transition-colors`}
                      data-testid={`pricing-card-${index}`}
                    >
                      <CardHeader className="text-center pb-4">
                        <CardTitle className="text-xl font-semibold text-[#0C0A09]">
                          {plan.name}
                        </CardTitle>
                        <p className="text-[#78716C] text-sm">{plan.description}</p>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        <div className="text-center py-4 border-y border-[#E7E5E4]">
                          <p className="text-4xl font-bold text-[#064E3B]">{plan.price}</p>
                          <p className="text-[#78716C] text-sm">{plan.frequency}</p>
                        </div>
                        <ul className="space-y-3">
                          {plan.features.map((feature, i) => (
                            <li key={i} className="flex items-start gap-3">
                              <CheckCircle2 className="w-5 h-5 text-[#064E3B] mt-0.5 flex-shrink-0" />
                              <span className="text-[#78716C] text-sm">{feature}</span>
                            </li>
                          ))}
                        </ul>
                        <Link to="/booking" className="block">
                          <Button
                            className={`w-full rounded-full py-6 font-medium ${
                              plan.popular
                                ? "bg-[#C0A062] hover:bg-[#B08D55] text-white"
                                : "bg-[#064E3B] hover:bg-[#065F46] text-white"
                            }`}
                          >
                            {plan.price === "Custom" ? "Get a Quote" : "Book Now"}
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>

          {/* Note */}
          <div className="mt-12 p-6 bg-[#F0FDF4] rounded-xl border border-[#064E3B]/20 flex items-start gap-4">
            <Info className="w-6 h-6 text-[#064E3B] flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-[#064E3B] mb-1">Pricing Note</p>
              <p className="text-[#78716C] text-sm">
                Prices shown are starting rates for standard-sized properties. Final pricing depends on property size, condition, and specific requirements. Contact us for a personalized quote tailored to your needs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Preview */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-[#0C0A09] mb-4">
              Frequently Asked Questions
            </h2>
            <div className="section-divider" />
          </div>

          <div className="space-y-6">
            {[
              {
                q: "What's included in a standard cleaning?",
                a: "Our standard cleaning covers all living areas including dusting, vacuuming, mopping, kitchen and bathroom cleaning, trash removal, and bed making. It's perfect for regular maintenance."
              },
              {
                q: "How do you determine pricing?",
                a: "Pricing is based on property size, condition, and service type. We provide free estimates after understanding your specific needs. There are no hidden fees."
              },
              {
                q: "Do you bring your own supplies?",
                a: "Yes! We bring all necessary cleaning supplies and equipment. We use eco-friendly, professional-grade products. Let us know if you have any preferences or sensitivities."
              },
              {
                q: "What's your cancellation policy?",
                a: "We require 24-hour notice for cancellations. Same-day cancellations may incur a fee. We understand emergencies happen and handle each situation individually."
              }
            ].map((faq, index) => (
              <div
                key={index}
                className="p-6 bg-[#FAFAF9] rounded-xl border border-[#E7E5E4]"
                data-testid={`faq-${index}`}
              >
                <h3 className="font-semibold text-[#0C0A09] mb-2">{faq.q}</h3>
                <p className="text-[#78716C] text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/contact">
              <Button variant="outline" className="border-[#064E3B] text-[#064E3B] hover:bg-[#064E3B] hover:text-white rounded-full px-8 py-6">
                Have More Questions?
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#064E3B]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
            Book your cleaning today or contact us for a custom quote. We're here to help make your space shine!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/booking" data-testid="pricing-book-btn">
              <Button className="bg-[#C0A062] hover:bg-[#B08D55] text-white rounded-full px-8 py-6 text-lg font-medium shadow-lg shadow-[#C0A062]/30">
                Book Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+16478700445" data-testid="pricing-call-btn">
              <Button className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#064E3B] rounded-full px-8 py-6 text-lg font-medium">
                <Phone className="mr-2 w-5 h-5" />
                Call (647) 870-0445
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
